import { useCallback, useEffect, useMemo, useState } from 'react';
import useInfiniteScroll from '../../common/hooks/useInfiniteScroll';

const initialPaginationQuery = {
  page: 1,
  perPage: 16,
  sortOrder: 'desc',
  sortBy: 'date',
  hasNextPage: true,
};

const initialQuery = {
  archived: false,
  folder: null,
  omitFiledStories: null,
  scheduled: null,
  includeEmpty: true,
};

export default function useFetchListWithInfiniteScrollV2({ service, bottomMinOffset, scrollContainer, filterOutIds = [] }) {
  const [list, setList] = useState([]);
  const [paginationQuery, setPaginationQuery] = useState(initialPaginationQuery);
  const [query, setQuery] = useState(initialQuery);
  const [loading, setLoading] = useState(false);
  const [moreLoading, setMoreLoading] = useState(false);
  const [totalData, setTotalData] = useState(0);
  const isEmptyList = useMemo(() => list.length === 0, [list]);

  const onSearch = useCallback((type, value) => setQuery(prevState => ({ ...prevState, [type]: value })), [
    query,
  ]);

  const searchInput = useMemo(() => ({ value: query.search, onChange: onSearch }), [onSearch, query.search]);

  const listFilteredById = useMemo(() => 
    filterOutIds.length ? list.filter(item => !filterOutIds.includes(item.id)) : list,
    [list, filterOutIds]
  );

  const fetchList = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const response = await service({
        perPage: paginationQuery.perPage,
        sortOrder: paginationQuery.sortOrder,
        sortBy: paginationQuery.sortBy, 
        page, 
        ...query });
      const { data, meta } = response;
      setTotalData(meta.total);
      setList(prev => (page === 1 ? data : [...prev, ...data]));
      setPaginationQuery(prev => ({ ...prev, page: meta.next ?? prev.page, hasNextPage: !!meta.next }));
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, [service, paginationQuery.perPage, query]);

  useEffect(() => {
    !loading && fetchList();
  }, [fetchList]);

  const loadMore = useCallback(async () => {
    if (!paginationQuery.hasNextPage) return;
    setMoreLoading(true);
    await fetchList(paginationQuery.page);
    setMoreLoading(false);
  }, [fetchList, paginationQuery]);

  const infiniteRef = useInfiniteScroll({
    hasNextPage: paginationQuery.hasNextPage,
    loading,
    onLoadMore: loadMore,
    threshold: 150,
    bottomMinOffset,
    scrollContainer,
  });

  return {
    infiniteRef,
    list,
    listFilteredById,
    setList,
    searchInput,
    totalData,
    isEmptyList,
    loading,
    moreLoading,
    fetchList,
    query,
  };
}
